/**
 * @format
 *
 */

import { useContext, useEffect, useMemo, useState } from 'react';

import moment from 'jalali-moment';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { EventContext } from '../../../Contexts/EventContext';
import Loading from '../../../Shared/Components/Loading';
import ModalCustom from '../../../Shared/Components/ModalCustom';
import Pagination from '../../../Shared/Components/Pagination';
import { error, success } from '../../../Shared/Components/Toast';
import useApi from '../../../Shared/hooks/api/Index';
import { useAccess } from '../../../Shared/hooks/useAccess';
import useQuery from '../../../Shared/queryString';
import { number_format } from '../../../Shared/utils/helper';
import Master from '../../Layouts/Master';

const modalStyles = {
  content: {
    width: '92%',
    maxWidth: '720px',
    maxHeight: '90vh',
    overflow: 'auto',
    background: '#fff',
    color: '#212529',
    padding: '1.25rem',
  },
};

const DocumentPreview = ({ title, url }) => (
  <div className='mb-4'>
    <div className='f-13 font-weight-bold text-secondary mb-2'>{title}</div>
    {url ? (
      <a href={url} target='_blank' rel='noopener noreferrer' className='d-block'>
        <img
          src={url}
          alt={title}
          className='img-fluid rounded border'
          style={{ maxHeight: '320px', objectFit: 'contain', background: '#f8f9fa' }}
        />
      </a>
    ) : (
      <div className='alert alert-warning f-13 mb-0'>بارگذاری نشده</div>
    )}
  </div>
);

const RegisterList = () => {
  const eventContext = useContext(EventContext);
  const { search } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [removeModal, setRemoveModal] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeId, setRemoveId] = useState(undefined);
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nationalCardId, setNationalCardId] = useState('');
  const { id } = useParams();
  const { canManageMatches } = useAccess();

  useEffect(() => {
    const params = new URLSearchParams(search);
    setName(params.get('name') || '');
    setLastName(params.get('last_name') || '');
    setNationalCardId(params.get('national_card_id') || '');
    getRegisterList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const getRegisterList = async () => {
    setLoading(true);
    await useApi.get(
      `/panel/match/${id}/register-list${search}`,
      {},
      (res) => {
        eventContext.userList = res.data;
        setLoading(false);
      },
      () => {
        setLoading(false);
        error('هنگام دریافت اطلاعات خطایی رخ داده است');
      },
    );
  };

  const buildFilterQuery = () => {
    const params = new URLSearchParams();
    if (name.trim()) params.set('name', name.trim());
    if (lastName.trim()) params.set('last_name', lastName.trim());
    if (nationalCardId.trim()) params.set('national_card_id', nationalCardId.trim());
    return params.toString();
  };

  const searchRegisters = () => {
    const query = buildFilterQuery();
    navigate(`/panel/matches/${id}/register-list${query ? `?${query}` : ''}`);
  };

  const clearFilters = () => {
    setName('');
    setLastName('');
    setNationalCardId('');
    navigate(`/panel/matches/${id}/register-list`);
  };

  const paginationBasePath = useMemo(() => {
    const query = useQuery.only('name', 'last_name', 'national_card_id');
    return `/panel/matches/${id}/register-list${query}`;
  }, [id, search]);

  const onDownloadList = () => {
    downloadRegisterList();
  };

  const downloadRegisterList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(search);
      params.set('export_excel', '1');
      await useApi.downloadFile(
        `/panel/match/${id}/register-list?${params.toString()}`,
      );
    } catch (err) {
      /* downloadFile already shows a toast */
    } finally {
      setLoading(false);
    }
  };

  const openDocumentsModal = (item) => {
    setSelectedRegister(item);
    setModalOpen(true);
  };

  const closeDocumentsModal = () => {
    setModalOpen(false);
    setSelectedRegister(null);
  };

  const removeRegisterUser = async (registerId) => {
    setRemoveLoading(true);
    await useApi.post(
      '/panel/match/remove-on-match',
      { register: registerId },
      (res) => {
        const { data } = res;
        if (data?.status === 422) {
          error(data?.message);
          setRemoveLoading(false);
          return;
        }
        if (data?.status === 200) {
          success('کاربر با موفقیت حذف شد');
          setRemoveModal(false);
          getRegisterList();
        }
        setRemoveLoading(false);
      },
      () => {
        error('عملیات با خطا مواجه شد');
        setRemoveLoading(false);
      },
    );
  };

  const selectedUser = selectedRegister?.user;
  const totalCount = eventContext.userList?.total ?? 0;

  return loading ? (
    <Loading />
  ) : (
    <Master>
      <div className='d-flex flex-row justify-content-between border-bottom pb-2 my-3'>
        <div className='d-flex flex-row mt-2 align-items-center flex-wrap'>
          <div className='d-flex align-items-center'>
            <i className='fad fa-list f-20 text-secondary mx-2'></i>
            <h1 className='text-secondary f-13 font-weight-bold mt-1 mb-0 p-0'>
              لیست شرکت کنندگان
            </h1>
            <span className='badge badge-secondary f-12 mx-2'>
              {number_format(totalCount)} نفر
            </span>
          </div>
          <div className='pl-3'>
            <button type='button' onClick={onDownloadList} className='btn btn-success'>
              دانلود خروجی اکسل
            </button>
          </div>
        </div>
      </div>

      <div className='d-flex flex-column flex-lg-row align-items-stretch align-items-lg-end w-100 my-3'>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0 flex-grow-1'>
          <label className='f-12 mb-1'>نام</label>
          <input
            type='text'
            className='form-control'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchRegisters()}
          />
        </div>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0 flex-grow-1'>
          <label className='f-12 mb-1'>نام خانوادگی</label>
          <input
            type='text'
            className='form-control'
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchRegisters()}
          />
        </div>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0 flex-grow-1'>
          <label className='f-12 mb-1'>کد ملی</label>
          <input
            type='text'
            className='form-control'
            value={nationalCardId}
            onChange={(e) => setNationalCardId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchRegisters()}
          />
        </div>
        <div className='d-flex flex-row mx-lg-2 mb-2 mb-lg-0'>
          <button
            type='button'
            className='btn btn-info text-white f-12'
            onClick={searchRegisters}
          >
            جستجو
          </button>
          <button
            type='button'
            className='btn btn-outline-secondary f-12 mx-2'
            onClick={clearFilters}
          >
            پاک کردن
          </button>
        </div>
      </div>

      <div className='w-100 overflow-auto'>
        {eventContext.userList?.data?.length > 0 ? (
          <table className='table table-hover'>
            <thead>
              <tr>
                <th>#</th>
                <th>نام</th>
                <th>نام خانوادگی</th>
                <th>موبایل</th>
                <th>کد ملی</th>
                <th>رده سنی</th>
                <th>استان</th>
                <th>تاریخ تولد</th>
                <th>تاریخ ثبت نام</th>
                <th>مدارک</th>
                {canManageMatches() && <th>حذف</th>}
              </tr>
            </thead>
            <tbody>
              {eventContext.userList?.data?.map((item, index) => (
                <tr key={item.id}>
                  <th>
                    {(eventContext.userList?.current_page - 1) *
                      eventContext.userList?.per_page +
                      (index + 1)}
                  </th>
                  <td>{item.user?.name}</td>
                  <td>{item.user?.last_name}</td>
                  <td>{item.user?.mobile}</td>
                  <td>{item.user?.national_card_id}</td>
                  <td>{item.match_age?.age?.title}</td>
                  <td>{item.user?.province?.name}</td>
                  <td>
                    {moment(item.user?.birthday ?? '1990-01-01', 'YYYY-MM-DD')
                      .locale('fa')
                      .format('YYYY/MM/DD')}
                  </td>
                  <td>
                    {moment(item.created_at ?? '1990-01-01', 'YYYY-MM-DD HH:mm')
                      .locale('fa')
                      .format('YYYY/MM/DD HH:mm')}
                  </td>
                  <td>
                    <div className='d-flex flex-column align-items-start'>
                      {!item.user?.profile_complete && (
                        <span className='badge badge-warning mb-2'>ناقص</span>
                      )}
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-info f-12'
                        onClick={() => openDocumentsModal(item)}
                      >
                        مشاهده
                      </button>
                    </div>
                  </td>
                  {canManageMatches() && (
                    <td>
                      <i
                        onClick={() => {
                          setRemoveId(item.id);
                          setRemoveModal(true);
                        }}
                        className='far fa-times border rounded-pill border-danger text-center d-flex flex-row align-items-center justify-content-center text-danger c-pointer'
                        style={{ width: '17px', height: '17px' }}
                        title='حذف ثبت نام'
                      ></i>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className='d-flex flex-row justify-content-center border p-3 my-3'>
            <span className='f-13 font-weight-bold text-secondary'>
              ثبت‌نامی یافت نشد
            </span>
          </div>
        )}
      </div>
      <div className='w-100 mt-4 d-flex'>
        <Pagination
          totalPage={eventContext.userList?.last_page}
          basePath={paginationBasePath}
        />
      </div>

      <ModalCustom
        modalIsOpen={modalOpen}
        onRequestClose={closeDocumentsModal}
        styles={modalStyles}
      >
        {selectedUser && (
          <>
            <div className='d-flex flex-row justify-content-between align-items-center border-bottom pb-2 mb-3'>
              <div>
                <h2 className='f-16 font-weight-bold mb-1 text-dark'>
                  مدارک ثبت‌نام
                </h2>
                <div className='f-13 text-secondary'>
                  {selectedUser.name} {selectedUser.last_name}
                  {' — '}
                  {selectedUser.national_card_id}
                </div>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-outline-secondary'
                onClick={closeDocumentsModal}
              >
                بستن
              </button>
            </div>

            {!selectedUser.profile_complete &&
              selectedUser.profile_missing_fields?.length > 0 && (
                <div className='alert alert-warning f-13'>
                  <div className='font-weight-bold mb-1'>مدارک/اطلاعات ناقص:</div>
                  <ul className='mb-0 pr-3'>
                    {selectedUser.profile_missing_fields.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}

            <DocumentPreview
              title='عکس پرسنلی'
              url={selectedUser.personnel_image}
            />
            <DocumentPreview
              title='تصویر بیمه ورزشی'
              url={selectedUser.insurance_image}
            />
          </>
        )}
      </ModalCustom>

      <ModalCustom modalIsOpen={removeModal}>
        <div className='d-flex flex-row justify-content-between align-items-center border-bottom pb-2'>
          <span className=''>حذف ثبت نام</span>
          <i
            className='far fa-times text-white f-20 mx-2 c-pointer'
            onClick={() => setRemoveModal(false)}
          ></i>
        </div>
        <div className='w-100 my-3'>
          <p className='text-center f-13 my-3 px-3'>
            آیا از حذف این ثبت‌نام مطمئن هستید؟ در صورت وجود پرداخت موفق، مبلغ
            پرداختی به کیف پول کاربر برمی‌گردد.
          </p>
          <div className='col-12 d-flex flex-row justify-content-center align-items-center'>
            <span
              className='btn px-5 btn-danger text-white f-12 my-3 mx-2 c-pointer'
              onClick={() => setRemoveModal(false)}
            >
              انصراف
            </span>
            {removeLoading ? (
              <button
                type='button'
                className='btn px-5 btn-info text-white f-12 my-3'
                disabled
              >
                <div
                  className='spinner-border spinner-border-sm text-white'
                  role='status'
                ></div>
              </button>
            ) : (
              <button
                type='button'
                onClick={() => removeRegisterUser(removeId)}
                className='btn px-5 btn-info text-white f-12 my-3'
              >
                حذف
              </button>
            )}
          </div>
        </div>
      </ModalCustom>
    </Master>
  );
};

export default RegisterList;
